import { type ReactNode } from "react";

import { type Conversation } from "shared/apiClient";
import { AssessmentLights } from "shared/types";
import { getText, interpolate } from "shared/lib";

import { ExternalLinkIcon } from "shared/components";

export interface MOIProps {
  assessmentLights: string;
  conversation: Conversation | undefined | null;
}

type LinkItem = {
  href: string;
  text: string;
};

function InlineLink({
  href,
  text,
}: {
  href: string;
  text: string;
}) {
  return (
    <a
      className="text-textLink hover:text-textLinkHover cursor-pointer underline"
      href={href}
      rel="noreferrer"
      tabIndex={0}
    >
      {text}
    </a>
  );
}

function NextStepLink({ href, text }: LinkItem) {
  return (
    <a
      className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
      href={href}
      rel="noreferrer"
      tabIndex={0}
      target="_blank"
    >
      {getText(text)}
      <ExternalLinkIcon className="ml-2 mt-[3px]" />
    </a>
  );
}

function NextStepItem({
  title,
  body,
  links,
  children,
}: {
  title: string;
  body: ReactNode;
  links?: LinkItem[];
  children?: ReactNode;
}) {
  return (
    <div>
      <h3 className="text-headingmd">{getText(title)}</h3>
      <div className="text-bodymd mb-2">
        {typeof body === "string" ? getText(body) : body}
      </div>
      {children}
      {!!links?.length && (
        <div className="flex flex-col items-start gap-1">
          {links.map((link) => (
            <NextStepLink key={`${link.href}-${link.text}`} {...link} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReportId({ conversation }: Pick<MOIProps, "conversation">) {
  return (
    <div className="border-interactiveBorder bg-interfaceWhite text-textPrimary text-bodylg w-min text-nowrap rounded border px-2 py-1.5 font-medium">
      {conversation?.identifier?.toUpperCase() || ""}
    </div>
  );
}

const landataLink = {
  href: "https://www.landata.online/",
  text: "Landata",
};

export function MOI({ assessmentLights, conversation }: MOIProps) {
  const renderRequestCertificateOfTitleGreen = () => {
    return (
      <NextStepItem
        title="Request a Certificate of Title"
        body="This tool doesn't account for covenants (restrictions), agreements, caveats, and easements which can impact what you can and can't do on a property. A Certificate of Title can include this information and can be obtained online via Landata Victoria."
        links={[landataLink]}
      />
    );
  };

  const renderCheckIfYouNeedABuildingPermit = () => {
    return (
      <NextStepItem
        title="Check if you need a building permit"
        body="This tool doesn't account for works requiring authorisation under the Building Regulations 2018. A building permit may be required for your proposal. Please contact Council's Building Department or a private building surveyor."
      />
    );
  };

  const renderApplyForInformationOnPlanningControls = () => {
    return (
      <NextStepItem
        title="Apply for information on planning controls"
        body={interpolate(
          "If you need formal written advice for your builder or surveyor confirming whether or not a planning permit is required, please lodge an enquiry for information on planning controls by emailing {{email}} with supporting documents and quoting your report ID.",
          {
            email: (
              <InlineLink
                href="mailto:info@moira.vic.gov.au"
                text="info@moira.vic.gov.au"
              />
            ),
          },
        )}
      >
        <ReportId conversation={conversation} />
      </NextStepItem>
    );
  };

  const renderDiscussYourProposalWithAPlanner = () => {
    return (
      <NextStepItem
        title="Discuss your proposal with a planner"
        body={interpolate(
          "Have questions? Contact us via email at {{email}} or speak to one of our planners on {{phone}}. When you contact us, please tell us your report ID.",
          {
            email: (
              <InlineLink
                href="mailto:info@moira.vic.gov.au"
                text="info@moira.vic.gov.au"
              />
            ),
            phone: (
              <InlineLink href="tel:0358719222" text="(03) 5871 9222" />
            ),
          },
        )}
      >
        <ReportId conversation={conversation} />
      </NextStepItem>
    );
  };

  const renderRefineYourProposal = () => {
    return (
      <NextStepItem
        title="Refine your proposal"
        body="Please review your myLot report to identify any outstanding or incomplete responses. Providing the required information will enable a more complete assessment and assist in finalising the advice provided."
      />
    );
  };

  const renderSeekPreApplicationAdvice = () => {
    return (
      <NextStepItem
        title="Seek pre-application advice"
        body={interpolate(
          "For more complex or substantial proposals, it is strongly recommended that you seek Pre-application Advice to better understand the applicable planning controls, constraints and key considerations. A Pre-application Advice request enables you to obtain preliminary feedback, which may include written advice and/or meetings with Council officers. Please note that supporting documentation is required as part of the pre-application process. You can book a pre-application meeting by emailing {{email}}. You should also quote your myLot enquiry ID when lodging your request.",
          {
            email: (
              <InlineLink
                href="mailto:info@moira.vic.gov.au"
                text="info@moira.vic.gov.au"
              />
            ),
          },
        )}
      >
        <ReportId conversation={conversation} />
      </NextStepItem>
    );
  };

  const renderRequestCertificateOfTitleBlue = () => {
    return (
      <NextStepItem
        title="Request a Certificate of Title"
        body="This tool does not assess title-related matters, including covenants (restrictions), agreements, caveats, and easements, which may affect what can and cannot be done on the land. A current Certificate of Title, including a copy of the plan, must be obtained to identify any applicable restrictions. The title must be dated within the last three (3) months to ensure the information is current. A Certificate of Title can be obtained online via Landata Victoria or through Council; please note a fee is charged for this service. Once received, you may choose to send an enquiry or application to info@moira.vic.gov.au with the title documents attached."
        links={[landataLink]}
      />
    );
  };

  const renderRequestCertificateOfTitleYellow = () => {
    return (
      <NextStepItem
        title="Request a Certificate of Title"
        body="This tool does not assess title-related matters, including covenants, agreements, caveats, and easements, which may affect what can and cannot be done on the land. A current Certificate of Title, including a copy of the plan, must be obtained when you are applying for a planning permit. The title must be dated within the last three (3) months to ensure the information is current. A Certificate of Title can be obtained online via Landata Victoria."
        links={[landataLink]}
      />
    );
  };

  const renderDiscussYourProposalWithAPlannerRed = () => {
    return (
      <NextStepItem
        title="Discuss your proposal with a planner"
        body="Based on the available information, the proposed use or development may be prohibited under the applicable planning controls. If you still have questions, you may submit a new enquiry or contact Moira Shire Council's Planning Department to discuss your proposal and obtain further guidance on the relevant provisions and potential pathways. When making contact, please quote your myLot report ID to assist Council officers in responding to your enquiry."
      >
        <ReportId conversation={conversation} />
      </NextStepItem>
    );
  };

  switch (assessmentLights) {
    case AssessmentLights.NoPermitRequired:
      return (
        <>
          {renderRequestCertificateOfTitleGreen()}
          {renderCheckIfYouNeedABuildingPermit()}
          {renderApplyForInformationOnPlanningControls()}
          {renderDiscussYourProposalWithAPlanner()}
        </>
      );
    case AssessmentLights.MoreInformationRequired:
      return (
        <>
          {renderRefineYourProposal()}
          {renderSeekPreApplicationAdvice()}
          {renderRequestCertificateOfTitleBlue()}
          {renderDiscussYourProposalWithAPlanner()}
        </>
      );
    case AssessmentLights.RequiresPermit:
      return (
        <>
          {renderRequestCertificateOfTitleYellow()}
          {renderApplyForInformationOnPlanningControls()}
          {renderSeekPreApplicationAdvice()}
        </>
      );
    case AssessmentLights.Prohibited:
      return <>{renderDiscussYourProposalWithAPlannerRed()}</>;
  }
}
